// =============================================================================
// components/glass/ActionSheet.tsx — DARK bottom sheet: a selectable list (each
// row shows a trailing check when picked) with a PINNED gradient primary
// GlassButton CTA at the bottom.
//
// Reference: the IG share sheet — a bottom-anchored dark-glass sheet of tappable
// rows plus a persistent primary action pinned above the home indicator.
//
// Apple-design SKILL alignment:
//   §12 dim-to-focus — modal scrim + the most-opaque glass surface (sheet).
//   §7  symmetric path — enters + exits from the bottom (Modal slide + spring).
//   §14 reduced motion/transparency handled here (frostier fill, cross-fade).
//
// Built from existing primitives: GlassSheet (mode='dark') for the body,
// GlassButton (variant='primary') for the gradient CTA.
// =============================================================================
import React, { useEffect, useRef } from 'react';
import {
  Modal, View, Text, Pressable, ScrollView, Animated, StyleSheet,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons, IoniconName } from '../ui/Icon';
import GlassSheet from './GlassSheet';
import GlassButton from './GlassButton';
import { glassColorTokens } from './tokens';
import { colors, spacing, fonts, animationTokens } from '../../constants/theme';
import { useReducedMotionTransparency } from '../ui/useReducedMotionTransparency';

export interface ActionSheetOption {
  key: string;
  label: string;
  sublabel?: string;
  icon?: IoniconName;
}

export interface ActionSheetProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  options: ActionSheetOption[];
  /** Selected option key(s). String = single-select, array = multi-select. */
  selected?: string | string[] | null;
  onSelect: (key: string) => void;
  /** Pinned primary CTA. Omit to hide the button. */
  ctaTitle?: string;
  onCta?: () => void;
  ctaLoading?: boolean;
  ctaDisabled?: boolean;
}

function isSelected(selected: ActionSheetProps['selected'], key: string): boolean {
  if (Array.isArray(selected)) return selected.includes(key);
  return selected === key;
}

export default function ActionSheet({
  visible, onClose, title, options, selected, onSelect,
  ctaTitle, onCta, ctaLoading, ctaDisabled,
}: ActionSheetProps) {
  const insets = useSafeAreaInsets();
  const { reduceMotion, reduceTransparency } = useReducedMotionTransparency();

  const anim = useRef(new Animated.Value(0)).current; // 0 hidden → 1 shown

  useEffect(() => {
    if (visible) {
      if (reduceMotion) {
        Animated.timing(anim, {
          toValue: 1, duration: animationTokens.duration.base, useNativeDriver: true,
        }).start();
      } else {
        // §4 drawer/sheet spring — gentle settle, no overshoot.
        Animated.spring(anim, {
          toValue: 1,
          useNativeDriver: true,
          damping: animationTokens.spring.damping,
          stiffness: animationTokens.spring.stiffness,
          mass: animationTokens.spring.mass,
        }).start();
      }
    } else {
      Animated.timing(anim, {
        toValue: 0, duration: animationTokens.duration.fast, useNativeDriver: true,
      }).start();
    }
  }, [visible, reduceMotion, anim]);

  const scrimOpacity = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, reduceTransparency ? 0.7 : 0.55],
  });
  // §7 enter/exit along the same (bottom) path.
  const translateY = anim.interpolate({ inputRange: [0, 1], outputRange: [reduceMotion ? 0 : 40, 0] });

  const sheetFill = reduceTransparency
    ? 'rgba(20,20,32,0.99)'
    : glassColorTokens.dark.surface.sheet;

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={onClose}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityLabel="Close sheet">
        <Animated.View style={[styles.scrim, { opacity: scrimOpacity }]} pointerEvents="none" />
      </Pressable>

      <Animated.View
        style={[styles.sheetWrap, { opacity: anim, transform: [{ translateY }] }]}
        pointerEvents="box-none"
      >
        <GlassSheet
          mode="light"
          surfaceColor={sheetFill}
          contentStyle={[styles.sheetContent, { paddingBottom: Math.max(insets.bottom, spacing.lg) }]}
        >
          {title ? <Text style={styles.title} numberOfLines={1}>{title}</Text> : null}

          <ScrollView
            style={styles.list}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
            bounces={false}
          >
            {options.map((o) => {
              const sel = isSelected(selected, o.key);
              return (
                <Pressable
                  key={o.key}
                  onPress={() => onSelect(o.key)}
                  style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
                  accessibilityRole="button"
                  accessibilityState={{ selected: sel }}
                  accessibilityLabel={o.label}
                >
                  {o.icon ? (
                    <View style={styles.iconWrap}>
                      <Ionicons name={o.icon} size={19} color="rgba(255,255,255,0.9)" />
                    </View>
                  ) : null}
                  <View style={styles.rowText}>
                    <Text style={[styles.label, sel && styles.labelSelected]} numberOfLines={1}>{o.label}</Text>
                    {o.sublabel ? <Text style={styles.sublabel} numberOfLines={1}>{o.sublabel}</Text> : null}
                  </View>
                  {sel ? (
                    <Ionicons name="checkmark-circle" size={22} color={colors.primaryLight} />
                  ) : (
                    <View style={styles.checkPlaceholder} />
                  )}
                </Pressable>
              );
            })}
          </ScrollView>

          {ctaTitle && onCta ? (
            <View style={styles.cta}>
              <GlassButton
                title={ctaTitle}
                variant="primary"
                size="lg"
                fullWidth
                onPress={onCta}
                loading={ctaLoading}
                disabled={ctaDisabled}
              />
            </View>
          ) : null}
        </GlassSheet>
      </Animated.View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  scrim: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#05050A' },
  sheetWrap: { position: 'absolute', left: 0, right: 0, bottom: 0, paddingHorizontal: spacing.md },
  sheetContent: { paddingTop: spacing.sm },
  title: {
    ...fonts.bold, fontSize: 16, color: colors.white,
    textAlign: 'center', marginBottom: spacing.md,
  },
  list: { maxHeight: 380 },
  listContent: { paddingBottom: spacing.xs },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xs,
  },
  rowPressed: { backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 12 },
  iconWrap: {
    width: 34, height: 34, borderRadius: 11,
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center', justifyContent: 'center',
  },
  rowText: { flex: 1 },
  label: { ...fonts.semibold, fontSize: 15, color: 'rgba(255,255,255,0.92)' },
  labelSelected: { color: colors.white },
  sublabel: { ...fonts.regular, fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 },
  checkPlaceholder: { width: 22, height: 22 },
  cta: { marginTop: spacing.md },
});
